import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "../components/Spinner";
import PasswordInput from "../components/PasswordInput";

export default function ResetPassword() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.resetPassword(token, password),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      nav("/");
    },
    onError: (e) => setErr(parseErr(e)),
  });

  return (
    <div className="relative grid h-full place-items-center bg-bg p-6 overflow-hidden">
      <div className="auth-glow" aria-hidden="true" />
      <div className="card auth-card max-w-md w-full p-8 space-y-5 relative">
        <h1 className="text-xl font-semibold text-center">Choose a new password</h1>
        <form
          className="space-y-3"
          onSubmit={(e) => { e.preventDefault(); setErr(null); reset.mutate(); }}
        >
          <PasswordInput
            required
            minLength={8}
            placeholder="New password (8+ chars)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
          />
          {err && (
            <div className="form-error" role="alert">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 mt-0.5">
                <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
              </svg>
              <span>{err}</span>
            </div>
          )}
          <LoadingButton
            type="submit"
            className="btn-primary w-full justify-center"
            pending={reset.isPending}
            pendingLabel="Resetting…"
          >
            Reset password
          </LoadingButton>
        </form>
      </div>
    </div>
  );
}

function parseErr(e: unknown): string {
  const m = String((e as Error).message ?? "");
  if (m.includes("invalid_token")) return "This reset link is invalid or has expired.";
  if (m.includes("bad_input")) return "Password must be at least 8 characters.";
  return "Couldn't reset your password.";
}
